import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, Undo2, Loader2 } from 'lucide-react';
import { previewCapacidad } from '../api';
import { resumenDias, festivosQueAfectan, mesesAVista } from './diasResumen';

/**
 * Paso 4 — Calendario visual de días disponibles.
 *
 * Click en día disponible -> excluido-manual. Click otra vez -> disponible.
 * Los festivos NO se pasan como prop; el conteo real lo da el backend en el
 * paso 7 (preview-capacidad). Esto evita una llamada extra.
 *
 * Rediseño (Fase 2): dos meses a la vez y un panel con el resumen (días disponibles, excluidos a mano
 * con «Reincluir», festivos que quitan días). Solo presentación: excluir y reincluir siguen siendo
 * TOGGLE_DIA_EXCLUIDO y «Restaurar» CLEAR_DIAS_EXCLUIDOS, como antes (hotfix de exclusiones intacto).
 * Los días con festivos los da preview-capacidad (la misma API que el paso 7), que necesita técnicos.
 */
const ESPERA_PREVIEW_MS = 500;

export default function Paso4DiasDisponibles({ payload, dispatch }) {
  const { fecha_inicio, fecha_limite, excluir_fines_de_semana, permite_fin_semana, excluidos_manuales } = payload;
  const hayTecnicos = (payload.tecnicos_ids || []).length > 0;

  const resumen = useMemo(
    () => resumenDias({ fecha_inicio, fecha_limite, excluir_fines_de_semana, excluidos_manuales }),
    [fecha_inicio, fecha_limite, excluir_fines_de_semana, excluidos_manuales],
  );

  // Días disponibles según el servidor (ya sin festivos). Solo lectura; con una espera para no
  // llamar en cada clic.
  const [servidor, setServidor] = useState({ cargando: false, dias: null });
  const clavePreview = JSON.stringify([
    fecha_inicio, fecha_limite, excluir_fines_de_semana, excluidos_manuales, payload.tecnicos_ids,
    payload.duracion_cita, payload.horario_inicio, payload.horario_fin, payload.ambito,
  ]);
  useEffect(() => {
    if (!fecha_inicio || !fecha_limite || fecha_limite < fecha_inicio || !hayTecnicos) {
      setServidor({ cargando: false, dias: null });
      return undefined;
    }
    let vivo = true;
    setServidor((s) => ({ ...s, cargando: true }));
    const t = setTimeout(() => {
      previewCapacidad({
        fecha_inicio,
        fecha_limite,
        horario_inicio: payload.horario_inicio,
        horario_fin: payload.horario_fin,
        duracion_cita: payload.duracion_cita,
        excluir_fines_de_semana,
        excluir_festivos: payload.excluir_festivos,
        excluidos_manuales,
        tecnicos_ids: payload.tecnicos_ids,
        ambito: payload.ambito,
      })
        .then((r) => { if (vivo) setServidor({ cargando: false, dias: Number.isInteger(r?.dias_totales) ? r.dias_totales : null }); })
        .catch(() => { if (vivo) setServidor({ cargando: false, dias: null }); });
    }, ESPERA_PREVIEW_MS);
    return () => { vivo = false; clearTimeout(t); };
  }, [clavePreview]); // eslint-disable-line react-hooks/exhaustive-deps -- la clave resume las dependencias

  const primerMes = useMemo(() => {
    if (!fecha_inicio) return null;
    const [y, m] = fecha_inicio.split('-').map(Number);
    return { year: y, month: m };
  }, [fecha_inicio]);

  const ultimoMes = useMemo(() => {
    if (!fecha_limite) return null;
    const [y, m] = fecha_limite.split('-').map(Number);
    return { year: y, month: m };
  }, [fecha_limite]);

  const [mesActual, setMesActual] = useState(primerMes);

  React.useEffect(() => {
    if (primerMes && (!mesActual ||
      mesActual.year * 12 + mesActual.month < primerMes.year * 12 + primerMes.month ||
      mesActual.year * 12 + mesActual.month > (ultimoMes?.year || 0) * 12 + (ultimoMes?.month || 0)
    )) {
      setMesActual(primerMes);
    }
  }, [fecha_inicio, fecha_limite]); // eslint-disable-line

  if (!fecha_inicio || !fecha_limite) {
    return (
      <div className="text-sm text-slate-500 max-w-2xl">
        Define la fecha de inicio y la fecha límite en el paso anterior para ver
        el calendario.
      </div>
    );
  }

  if (!mesActual) return null;

  const meses = mesesAVista(mesActual, ultimoMes);
  const ultimoVisible = meses[meses.length - 1];
  const puedeIrAtras =
    primerMes &&
    (mesActual.year > primerMes.year ||
      (mesActual.year === primerMes.year && mesActual.month > primerMes.month));
  const puedeIrAdelante =
    ultimoMes &&
    (ultimoVisible.year < ultimoMes.year ||
      (ultimoVisible.year === ultimoMes.year && ultimoVisible.month < ultimoMes.month));

  function navega(delta) {
    let m = mesActual.month + delta;
    let y = mesActual.year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMesActual({ year: y, month: m });
  }

  const excluidosSet = new Set(excluidos_manuales);
  const nFestivos = festivosQueAfectan(resumen.disponiblesAntesDeFestivos, servidor.dias);
  const reincluir = (fecha) => dispatch({ type: 'TOGGLE_DIA_EXCLUIDO', fecha });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
    <div className="space-y-4 min-w-0">
      <p className="text-sm text-slate-500">
        Toca cualquier día disponible para excluirlo. Vuelve a tocarlo para
        reincluirlo. Los fines de semana y festivos siguen las exclusiones del
        paso anterior.
      </p>

      {/* Fase 19a · #17 — permitir reservas en fin de semana. El calendario se
          actualiza al instante porque excluir_fines_de_semana se deriva del toggle. */}
      <label className="inline-flex items-center gap-2 py-2 px-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={permite_fin_semana ?? false}
          onChange={(e) => dispatch({
            type: 'TOGGLE_PERMITE_FIN_SEMANA',
            value: e.target.checked,
          })}
          className="rounded border-slate-300 text-primary focus:ring-primary"
        />
        <span className="text-sm text-slate-700">
          Permitir reservas en <strong>sábado y domingo</strong>
        </span>
      </label>

      <div className="flex items-center justify-between gap-2 max-w-3xl">
        <button
          type="button"
          onClick={() => navega(-1)}
          disabled={!puedeIrAtras}
          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Mes anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-500">
          {meses.length > 1 ? 'Dos meses a la vista' : 'Último mes de la campaña'}
        </span>
        <button
          type="button"
          onClick={() => navega(1)}
          disabled={!puedeIrAdelante}
          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Mes siguiente"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 max-w-3xl">
        {meses.map((m) => (
          <div key={`${m.year}-${m.month}`}>
            <div className="mb-1.5 text-center text-sm font-medium text-slate-800">
              {nombreMes(m.month)} {m.year}
            </div>
            <Calendario
              year={m.year}
              month={m.month}
              fechaInicio={fecha_inicio}
              fechaLimite={fecha_limite}
              excluirFindes={excluir_fines_de_semana}
              excluidos={excluidosSet}
              onToggle={(fecha) => dispatch({ type: 'TOGGLE_DIA_EXCLUIDO', fecha })}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs text-slate-500 max-w-3xl">
        <div>
          {excluidos_manuales.length === 0
            ? 'Sin días excluidos manualmente.'
            : `${excluidos_manuales.length} día(s) excluido(s) manualmente.`}
        </div>
        {excluidos_manuales.length > 0 && (
          <button
            type="button"
            onClick={() => dispatch({ type: 'CLEAR_DIAS_EXCLUIDOS' })}
            className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Restaurar
          </button>
        )}
      </div>

      <Leyenda />
    </div>

    {/* Panel lateral: el resumen */}
    <aside className="rounded-v25-md border border-v25-line bg-v25-surface-sunken p-4 space-y-4 text-sm" aria-label="Resumen de días">
      <div>
        <div className="text-xs uppercase tracking-wide text-v25-ink-500">Días disponibles</div>
        {servidor.dias != null ? (
          <div className="text-2xl font-semibold tabular-nums text-v25-ink-900">{servidor.dias}</div>
        ) : (
          <div className="text-2xl font-semibold tabular-nums text-v25-ink-900">
            {resumen.disponiblesAntesDeFestivos}
            <span className="ml-1 text-xs font-normal text-v25-ink-500">antes de festivos</span>
          </div>
        )}
        {servidor.cargando && (
          <div className="text-xs text-v25-ink-500"><Loader2 className="w-3 h-3 inline-block animate-spin mr-1" />Calculando…</div>
        )}
        <dl className="mt-2 space-y-0.5 text-xs text-v25-ink-700">
          <Linea t="Días del rango" v={resumen.totales} />
          {excluir_fines_de_semana && <Linea t="Fines de semana" v={`−${resumen.findes}`} />}
          <Linea t="Excluidos a mano" v={`−${resumen.manuales}`} />
          <Linea
            t="Festivos"
            v={nFestivos != null ? `−${nFestivos}` : '—'}
          />
        </dl>
        {nFestivos == null && !servidor.cargando && (
          <p className="mt-1 text-[11px] text-v25-ink-500">
            {hayTecnicos
              ? 'No se pudo calcular cuántos festivos caen en el rango.'
              : 'Los festivos se descuentan al elegir técnicos (los cuenta el servidor).'}
          </p>
        )}
        {nFestivos > 0 && (
          <p className="mt-1 text-[11px] text-v25-ink-500">
            Las fechas de los festivos no llegan a esta pantalla; solo cuántos quitan días.
          </p>
        )}
      </div>

      <div>
        <div className="mb-1 text-xs uppercase tracking-wide text-v25-ink-500">
          Excluidos a mano ({resumen.excluidos.length})
        </div>
        {resumen.excluidos.length === 0 ? (
          <p className="text-xs text-v25-ink-500">Ninguno.</p>
        ) : (
          <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
            {resumen.excluidos.map((x) => (
              <li key={x.fecha} className="flex items-center justify-between gap-2 rounded-v25-sm bg-white px-2 py-1 ring-1 ring-v25-line">
                <span className="min-w-0">
                  <span className="tabular-nums text-v25-ink-900">{fechaLarga(x.fecha)}</span>
                  {!x.enRango && <span className="block text-[11px] text-v25-ink-500">Fuera del rango</span>}
                </span>
                <button
                  type="button"
                  onClick={() => reincluir(x.fecha)}
                  className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs text-v25-brand-600 hover:bg-v25-brand-600/5"
                  title="Volver a dejar este día disponible"
                >
                  <Undo2 className="w-3 h-3" /> Reincluir
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
    </div>
  );
}

function Linea({ t, v }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt>{t}</dt>
      <dd className="tabular-nums">{v}</dd>
    </div>
  );
}

function fechaLarga(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-ES', {
    weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC',
  });
}

function Calendario({ year, month, fechaInicio, fechaLimite, excluirFindes, excluidos, onToggle }) {
  const primerDiaMes = new Date(Date.UTC(year, month - 1, 1));
  const ultimoDiaMes = new Date(Date.UTC(year, month, 0));
  const primerDow = (primerDiaMes.getUTCDay() + 6) % 7; // lunes=0
  const totalDias = ultimoDiaMes.getUTCDate();

  const celdas = [];
  for (let i = 0; i < primerDow; i++) celdas.push(null);
  for (let d = 1; d <= totalDias; d++) celdas.push(d);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 text-[10px] text-slate-500 uppercase tracking-wide">
        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
          <div key={d} className="px-2 py-1.5 text-center font-medium">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-100">
        {celdas.map((dia, idx) => {
          if (dia === null) {
            return <div key={idx} className="bg-white aspect-square" />;
          }
          const fechaStr = `${year}-${String(month).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
          const props = clasificarDia({ fecha: fechaStr, fechaInicio, fechaLimite, excluirFindes, excluidos, year, month, dia });
          return (
            <DiaCelda
              key={fechaStr}
              dia={dia}
              clase={props.clase}
              titulo={props.titulo}
              clickable={props.clickable}
              onClick={() => props.clickable && onToggle(fechaStr)}
            />
          );
        })}
      </div>
    </div>
  );
}

function DiaCelda({ dia, clase, titulo, clickable, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      title={titulo}
      className={
        'aspect-square text-xs flex items-center justify-center transition-colors ' +
        clase +
        (clickable ? ' cursor-pointer' : ' cursor-default')
      }
    >
      {dia}
    </button>
  );
}

function clasificarDia({ fecha, fechaInicio, fechaLimite, excluirFindes, excluidos, year, month, dia }) {
  if (fecha < fechaInicio || fecha > fechaLimite) {
    return { clase: 'bg-slate-50 text-slate-300', clickable: false, titulo: 'Fuera del rango' };
  }
  const d = new Date(Date.UTC(year, month - 1, dia));
  const dow = d.getUTCDay();
  const esFinde = dow === 0 || dow === 6;
  const esExcluido = excluidos.has(fecha);

  if (esFinde && excluirFindes) {
    return {
      clase: 'bg-slate-50 text-slate-400 line-through',
      clickable: false,
      titulo: 'Fin de semana (excluido por configuración)',
    };
  }
  if (esExcluido) {
    return {
      clase: 'bg-red-50 text-red-600 line-through hover:bg-red-100',
      clickable: true,
      titulo: 'Excluido manualmente — click para reincluir',
    };
  }
  return {
    clase: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    clickable: true,
    titulo: 'Disponible — click para excluir',
  };
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function nombreMes(m) {
  return MESES[m - 1] || '';
}

function Leyenda() {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
      <LeyendaItem clase="bg-emerald-50 text-emerald-700" label="Disponible" />
      <LeyendaItem clase="bg-red-50 text-red-600 line-through" label="Excluido manualmente" />
      <LeyendaItem clase="bg-slate-50 text-slate-400 line-through" label="Fin de semana / fuera de rango" />
    </div>
  );
}

function LeyendaItem({ clase, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-5 h-5 rounded text-center text-[10px] leading-5 ${clase}`}>·</span>
      <span>{label}</span>
    </div>
  );
}
